// GroupMembers Component - Members list for a group

import React from 'react';
import { User, Crown, Shield, UserMinus } from 'lucide-react';
import type { GroupMember, GroupMemberRole } from '../../types/groups';
import { Timestamp } from '@firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface GroupMembersProps {
  groupId: string;
  members: GroupMember[];
  currentUserRole?: GroupMemberRole;
  onViewProfile?: (userId: string) => void;
}

const getRoleBadge = (role: GroupMemberRole) => {
  switch (role) {
    case 'creator':
      return { icon: Crown, label: 'Gruender', color: 'text-yellow-600 bg-yellow-100' };
    case 'admin':
      return { icon: Shield, label: 'Admin', color: 'text-purple-600 bg-purple-100' };
    case 'moderator':
      return { icon: Shield, label: 'Moderator', color: 'text-blue-600 bg-blue-100' };
    default:
      return null;
  }
};

export function GroupMembers({
  groupId,
  members,
  currentUserRole,
  onViewProfile,
}: GroupMembersProps) {
  const isAdmin = currentUserRole === 'creator' || currentUserRole === 'admin';

  // Sort members: creator first, then admins, then by join date
  const sortedMembers = [...members].sort((a, b) => {
    const roleOrder = { creator: 0, admin: 1, moderator: 2, member: 3 };
    const roleCompare = roleOrder[a.role] - roleOrder[b.role];
    if (roleCompare !== 0) return roleCompare;

    const aDate = a.joinedAt instanceof Timestamp ? a.joinedAt.toDate() : a.joinedAt;
    const bDate = b.joinedAt instanceof Timestamp ? b.joinedAt.toDate() : b.joinedAt;
    return aDate.getTime() - bDate.getTime();
  });

  const formatJoinDate = (date: Timestamp | Date) => {
    const d = date instanceof Timestamp ? date.toDate() : date;
    return formatDistanceToNow(d, { locale: de, addSuffix: true });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">
          {members.length} {members.length === 1 ? 'Mitglied' : 'Mitglieder'}
        </h3>
      </div>

      <div className="divide-y divide-gray-100">
        {sortedMembers.map((member) => {
          const roleBadge = getRoleBadge(member.role);

          return (
            <div
              key={member.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
            >
              {/* Avatar */}
              {member.photoURL ? (
                <img
                  src={member.photoURL}
                  alt={member.displayName}
                  onClick={() => onViewProfile?.(member.userId)}
                  className="w-12 h-12 rounded-full object-cover cursor-pointer hover:opacity-80"
                />
              ) : (
                <div
                  onClick={() => onViewProfile?.(member.userId)}
                  className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200"
                >
                  <User className="w-6 h-6 text-gray-400" />
                </div>
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onViewProfile?.(member.userId)}
                    className="font-medium text-gray-900 hover:text-indigo-600 truncate"
                  >
                    {member.displayName}
                  </button>
                  {roleBadge && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${roleBadge.color}`}
                    >
                      <roleBadge.icon className="w-3 h-3" />
                      {roleBadge.label}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Beigetreten {formatJoinDate(member.joinedAt)}
                </p>
              </div>

              {/* Admin actions */}
              {isAdmin && member.role === 'member' && (
                <button className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                  <UserMinus className="w-5 h-5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {members.length === 0 && (
        <div className="p-8 text-center">
          <User className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Noch keine Mitglieder</p>
        </div>
      )}
    </div>
  );
}

export default GroupMembers;
